import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue } from 'recoil';
import { cn } from '../../common/utils';
import InboxZero from '../../components/InboxZero';
import { Button } from '../../components/actions/Button';
import NamedColor from '../../components/inputs/NamedColor';
import { ScreenTitle } from '../../components/layout/ScreenTitle';
import TitleSection from '../../components/layout/TitleSection';
import { ActionBar } from '../../components/nav/ActionBar';
import Tag from '../../helpContext/Tag';
import { namedSystemColors } from '../../store/namedColors.default';
import {
  namedColorsGroups,
  namedColorsGroupSelected,
} from '../../store/namedColors.state';
import { warehouseSelectedId } from '../../store/warehouse.state';
import NamedColorGroupSelector from '../NamedColorGroupSelector';
import useUpdateWarehouseExtraSettings from '../hooks/useUpdateWarehouseExtraSettings';

function useWarehouseNamedColorsWatcher() {
  const whId = useRecoilValue(warehouseSelectedId);
  const whNamedColorGroups = useRecoilValue(namedColorsGroups);
  const [updateWarehouseExtraSettings] = useUpdateWarehouseExtraSettings();
  const [localWhId, setLocalWhId] = useState<string>(null);

  useEffect(() => {
    if (_.isNil(whId)) return;
    if (localWhId !== whId) {
      setLocalWhId(whId);
    } else {
      updateWarehouseExtraSettings({
        patch: {
          namedColors: whNamedColorGroups,
        },
      });
    }
  }, [whNamedColorGroups]);
}

export type WarehouseSettingsParams = {
  warehouseId: string;
};

const WarehouseSettingsNamedColors: React.FC = () => {
  const { t } = useTranslation('app');
  const [selectedGroup, setSelectedGroup] = useRecoilState(
    namedColorsGroupSelected,
  );
  useWarehouseNamedColorsWatcher();

  function resetColorGroup() {
    setSelectedGroup({
      ...selectedGroup,
      colors: namedSystemColors,
    });
  }

  function removeNamedColor(key: string) {
    setSelectedGroup({
      ...selectedGroup,
      colors: _.omit(selectedGroup.colors, key),
    });
  }

  function resetNamedColor(key: string) {
    setSelectedGroup({
      ...selectedGroup,
      colors: { ...selectedGroup.colors, [key]: namedSystemColors[key] },
    });
  }

  const sortedSystemColors = _.orderBy(
    _.toPairs(selectedGroup?.colors).filter(([key]) =>
      _.has(namedSystemColors, key),
    ),
    ([key]) => key,
    ['asc'],
  );

  const sortedUserColors = _.orderBy(
    _.toPairs(selectedGroup?.colors).filter(
      ([key]) => !_.has(namedSystemColors, key),
    ),
    ([key]) => key,
    ['asc'],
  );

  const buttonsClass = cn(
    'hover:text-menu-active',
    'text-xxs',
    'flex items-center',
    'rounded-md',
    'px-2 py-1',
    'focus:outline-none focus:ring-0',
    'min-w-32',
  );

  return (
    <React.Fragment data-component="WarehouseSettingsNamedColors">
      <ScreenTitle
        hasPadding
        isHero
        title={t`Warehouse colors`}
        subtitle={t`Settings`}
        isSticky
      />

      <ActionBar stickyTop sticky className="flex px-2 py-1">
        <NamedColorGroupSelector noSelectionTitle={t`Select color group...`} />
        <Button
          label={t`Reset Color Group`}
          className={buttonsClass}
          buttonType="delete"
          buttonSize="xs"
          onPress={() => resetColorGroup()}
          isDisabled={_.isNil(selectedGroup)}
        />
      </ActionBar>

      <div className="gap-y-1 space-y-0.5 p-1">
        <TitleSection
          id={'system-colors-user'}
          inPanelView
          title={t`System Colors`}
          classNameInner="space-y-0.5"
          titleSummaryOpen={
            <Button
              label={t`Reset All`}
              className={buttonsClass}
              buttonType="delete"
              buttonSize="xs"
              onPress={() => resetColorGroup()}
              isDisabled={_.isEmpty(sortedSystemColors)}
            />
          }
          collapsible
        >
          {sortedSystemColors.length > 0 ? (
            _.map(sortedSystemColors, ([key, colors]) => (
              <div
                key={`named-color-row-${key}`}
                className={'bg-menu/10 flex items-center px-2 py-3'}
              >
                <NamedColor namedColorKey={key} className="!h-6 !w-6" />
                <div className="flex flex-1 gap-2 px-2">
                  <Tag title={t`System`}>{t`System`}</Tag>
                  <span>{key}</span>
                </div>

                <Button
                  label={t`Reset`}
                  className={buttonsClass}
                  buttonType="delete"
                  buttonSize="xs"
                  onPress={() => resetNamedColor(key)}
                />
              </div>
            ))
          ) : (
            <InboxZero message={t`No system colors available`}></InboxZero>
          )}
        </TitleSection>

        <TitleSection
          id={'settings-colors-user'}
          inPanelView
          title={t`User Colors`}
          classNameInner="space-y-0.5"
          titleSummaryOpen={
            <Button
              label={t`Delete All`}
              className={buttonsClass}
              buttonType="delete"
              buttonSize="xs"
              onPress={() => {
                const userColorKeys = sortedUserColors.map(([key]) => key);
                userColorKeys.forEach(key => removeNamedColor(key));
              }}
              isDisabled={_.isEmpty(sortedUserColors)}
            />
          }
          collapsible
        >
          {sortedUserColors.length > 0 ? (
            _.map(sortedUserColors, ([key, colors]) => (
              <div
                key={`named-color-row-${key}`}
                className={'bg-menu/10 flex items-center px-2 py-3'}
              >
                <NamedColor namedColorKey={key} />
                <div className="flex flex-1 gap-2 px-2 text-xs">
                  <span>{key}</span>
                </div>
                <Button
                  label={t`Delete`}
                  className={buttonsClass}
                  buttonType="delete"
                  buttonSize="xs"
                  onPress={() => removeNamedColor(key)}
                />
              </div>
            ))
          ) : (
            <InboxZero message={t`No user colors defined`}></InboxZero>
          )}
        </TitleSection>
      </div>
    </React.Fragment>
  );
};

export default WarehouseSettingsNamedColors;
